import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Crosshair, LayoutList, Loader2, Search, X } from "lucide-react";

import { getProperties } from "../../../api/agent";
import { formatShortAddress } from "../../../utils/addressFormatter";
import PropertyCard from "../components/PropertyCard";

/** Public Mapbox token (pk.*) — create under Account → Access tokens at mapbox.com */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";

const DEFAULT_CENTER = { lng: 144.9631, lat: -37.8136 };

const MAP_STYLE = "mapbox://styles/mapbox/streets-v12";

const PRICE_OPTIONS = [
    { value: "", label: "Price" },
    { value: "max_500000", label: "Up to $500k" },
    { value: "500000_750000", label: "$500k – $750k" },
    { value: "750000_1000000", label: "$750k – $1m" },
    { value: "1000000_1500000", label: "$1m – $1.5m" },
    { value: "1500000_2000000", label: "$1.5m – $2m" },
    { value: "min_2000000", label: "$2m+" },
];

const PROPERTY_TYPE_OPTIONS = [
    { value: "", label: "Property type" },
    { value: "house", label: "House" },
    { value: "apartment", label: "Apartment / Unit" },
    { value: "townhouse", label: "Townhouse" },
    { value: "land", label: "Land" },
    { value: "commercial", label: "Commercial" },
];

const BED_OPTIONS = [
    { value: "", label: "Bed" },
    { value: "1", label: "1+" },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
    { value: "5", label: "5+" },
];

function priceBandToParams(band) {
    switch (band) {
        case "max_500000":
            return { min_price: null, max_price: "500000" };
        case "500000_750000":
            return { min_price: "500000", max_price: "750000" };
        case "750000_1000000":
            return { min_price: "750000", max_price: "1000000" };
        case "1000000_1500000":
            return { min_price: "1000000", max_price: "1500000" };
        case "1500000_2000000":
            return { min_price: "1500000", max_price: "2000000" };
        case "min_2000000":
            return { min_price: "2000000", max_price: null };
        default:
            return {};
    }
}

function toNum(v) {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/** Bounding boxes for each AU state/territory (W, S, E, N) — used to bias Mapbox to the right state. */
const STATE_BBOX = {
    VIC: [140.96, -39.2, 150.04, -33.98],
    NSW: [140.99, -37.51, 153.64, -28.16],
    QLD: [137.99, -29.18, 153.55, -9.22],
    SA: [129.0, -38.06, 141.0, -25.99],
    WA: [112.92, -35.13, 129.0, -13.69],
    TAS: [143.82, -43.65, 148.48, -39.57],
    NT: [129.0, -25.99, 138.0, -10.92],
    ACT: [148.76, -35.93, 149.4, -35.13],
};

function coordsInBbox(lat, lng, bbox) {
    const [w, s, e, n] = bbox;
    return lng >= w && lng <= e && lat >= s && lat <= n;
}

function hasValidCoords(p) {
    const lat = toNum(p.latitude);
    const lng = toNum(p.longitude);
    if (lat == null || lng == null || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        return false;
    }
    // Reject stored coords that disagree with the property's declared state — bad legacy
    // data (e.g. a VIC listing with NSW coords) gets re-geocoded with the correct bbox.
    const stateKey = (p.state || "").trim().toUpperCase();
    const bbox = STATE_BBOX[stateKey];
    if (bbox && !coordsInBbox(lat, lng, bbox)) return false;
    return true;
}

/** Expand common Australian street-type abbreviations so Mapbox can match routable streets. */
const STREET_TYPE_EXPANSIONS = {
    st: "Street", rd: "Road", cr: "Crescent", cres: "Crescent", cct: "Circuit",
    ct: "Court", dr: "Drive", drv: "Drive", ave: "Avenue", av: "Avenue",
    ln: "Lane", pl: "Place", pde: "Parade", tce: "Terrace", hwy: "Highway",
    blvd: "Boulevard", bvd: "Boulevard", cl: "Close", gr: "Grove", sq: "Square",
    wy: "Way", wlk: "Walk", esp: "Esplanade",
};

function normaliseStreetLine(line) {
    if (!line) return line;
    let out = line.replace(/^\s*(\d+[a-z]?)\s*&\s*\d+[a-z]?\s+/i, "$1 ");
    out = out.replace(/\b([A-Za-z]+)\.?\s*$/, (m, tok) => {
        const key = tok.toLowerCase();
        return STREET_TYPE_EXPANSIONS[key] || m;
    });
    return out;
}

/** Single-line AU query for Mapbox forward geocoding when lat/lng are missing. */
function buildGeocodeQuery(p) {
    if (!p) return "";
    const suburb = (p.suburb || p.city || "").trim();
    const state = (p.state || "").trim();
    const pc = (p.postal_code || p.postcode || "").trim();
    let street = (p.street_name || p.street || p.address || "").trim();
    street = normaliseStreetLine(street);

    const parts = [];
    if (street) parts.push(street);
    if (suburb) parts.push(suburb);
    if (state) parts.push(state);
    if (pc) parts.push(pc);
    if (parts.length === 0) return "";
    return `${parts.join(", ")}, Australia`;
}

/** Drop Buyer Portal "Sell Your Property" appraisal leads — they aren't real listings. */
function isBuyerPortalLead(item) {
    const description = (item.description || "").toLowerCase();
    const internalNotes = (item.internal_notes || "").toLowerCase();
    return description.includes("buyer portal") || internalNotes.includes("buyer portal");
}

/** Coordinates from CRM or from client-side geocode cache (`geocodedById[id] = { lat, lng } | null`). */
function resolveCoords(p, geocodedById) {
    if (hasValidCoords(p)) {
        return { lat: toNum(p.latitude), lng: toNum(p.longitude) };
    }
    const g = geocodedById[p.id];
    if (g && typeof g.lat === "number" && typeof g.lng === "number") {
        return { lat: g.lat, lng: g.lng };
    }
    return null;
}

const selectClass =
    "h-10 px-3 rounded-lg border border-gray-200 bg-white text-[13px] font-semibold text-[#36423F] min-w-[120px] max-w-full";

function MapView({ accessToken, propertiesWithCoords, totalCount, fitTrigger, onMarkerSelectRef }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);

    const [mapReady, setMapReady] = useState(false);
    const [mapError, setMapError] = useState(null);

    useEffect(() => {
        if (!containerRef.current || !accessToken) return undefined;

        mapboxgl.accessToken = accessToken;
        setMapError(null);

        const container = containerRef.current;

        const map = new mapboxgl.Map({
            container,
            style: MAP_STYLE,
            center: [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
            zoom: 10,
        });

        mapRef.current = map;

        const resizeMap = () => {
            try {
                map.resize();
            } catch {
                /* ignore */
            }
        };

        const resizeObserver = new ResizeObserver(() => resizeMap());
        resizeObserver.observe(container);

        const onLoad = () => {
            resizeMap();
            setMapReady(true);
        };
        const onErr = (e) => {
            const msg =
                e?.error?.message || e?.message || (typeof e === "string" ? e : null) || "Map failed to load tiles";
            setMapError(msg);
        };

        map.on("load", onLoad);
        map.on("error", onErr);

        return () => {
            resizeObserver.disconnect();
            map.off("load", onLoad);
            map.off("error", onErr);
            map.remove();
            mapRef.current = null;
            setMapReady(false);
        };
    }, [accessToken]);

    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map || propertiesWithCoords.length === 0) return;

        if (propertiesWithCoords.length === 1) {
            const p = propertiesWithCoords[0];
            map.easeTo({ center: [toNum(p.longitude), toNum(p.latitude)], zoom: 15 });
            return;
        }

        const bounds = new mapboxgl.LngLatBounds();
        propertiesWithCoords.forEach((p) => {
            bounds.extend([toNum(p.longitude), toNum(p.latitude)]);
        });
        map.fitBounds(bounds, { padding: 48, maxZoom: 15 });
    }, [mapReady, propertiesWithCoords, fitTrigger]);

    const markersRef = useRef([]);

    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map) return;

        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];

        propertiesWithCoords.forEach((p) => {
            const lng = toNum(p.longitude);
            const lat = toNum(p.latitude);
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

            const marker = new mapboxgl.Marker({ color: "#55C5D0", scale: 1.12 })
                .setLngLat([lng, lat])
                .addTo(map);

            const el = marker.getElement();
            if (el) {
                el.style.cursor = "pointer";
                el.style.zIndex = "3";
                el.addEventListener("click", (e) => {
                    e.stopPropagation();
                    onMarkerSelectRef?.current?.(p);
                });
            }
            markersRef.current.push(marker);
        });
    }, [mapReady, propertiesWithCoords, onMarkerSelectRef]);

    useEffect(() => {
        return () => {
            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];
        };
    }, []);

    const locateMe = () => {
        const map = mapRef.current;
        if (!map || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                map.easeTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 14 });
            },
            () => {},
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    return (
        <div className="absolute inset-0">
            <div ref={containerRef} className="absolute inset-0 h-full w-full bg-[#e8eef3]" />

            {mapError ? (
                <div className="absolute inset-0 z-[2] flex items-center justify-center bg-[#EEF2F6]/95 p-4 text-center text-[13px] text-[#B91C1C]">
                    Map error: {mapError}
                </div>
            ) : null}

            <div className="pointer-events-none absolute left-1/2 top-3 z-[1] max-w-[92vw] -translate-x-1/2 sm:max-w-none">
                <div className="pointer-events-none flex flex-col items-center gap-1 rounded-2xl border border-gray-100 bg-white px-4 py-2.5 text-center shadow-md">
                    <span className="text-[13px] font-semibold leading-tight text-[#36423F]">
                        <span className="text-[#55C5D0]">{propertiesWithCoords.length}</span> listings on the map
                        <span className="font-normal text-gray-500"> · {totalCount} match filters</span>
                    </span>
                    <span className="text-[11px] font-medium leading-snug text-gray-400">
                        Pins show each location (CRM coordinates or address lookup). Zoom in if pins sit on top of each
                        other.
                    </span>
                </div>
            </div>

            <button
                type="button"
                onClick={locateMe}
                disabled={!mapReady}
                className="absolute right-3 top-3 z-[1] flex h-10 w-10 items-center justify-center rounded-lg border border-gray-100 bg-white text-[#36423F] shadow-md hover:bg-gray-50 disabled:opacity-40"
                title="Centre on your location"
                aria-label="Centre on your location"
            >
                <Crosshair size={18} />
            </button>

            {propertiesWithCoords.length === 0 && totalCount > 0 ? (
                <div className="pointer-events-none absolute bottom-16 left-1/2 z-[1] max-w-md -translate-x-1/2 rounded-lg border border-gray-100 bg-white/95 px-4 py-2 text-center text-[12px] text-gray-500 shadow">
                    No pins yet — waiting for addresses to be located, or listing addresses need more detail in the CRM.
                </div>
            ) : null}
        </div>
    );
}

const CollingsPropertiesMapPage = () => {
    const navigate = useNavigate();

    const [mapCardProperty, setMapCardProperty] = useState(null);
    const onMarkerSelectRef = useRef(() => {});

    useEffect(() => {
        onMarkerSelectRef.current = (p) => setMapCardProperty(p);
    }, []);

    useEffect(() => {
        if (!mapCardProperty) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setMapCardProperty(null);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [mapCardProperty]);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [propertyType, setPropertyType] = useState("");
    const [priceBand, setPriceBand] = useState("");
    const [minBeds, setMinBeds] = useState("");

    const [properties, setProperties] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [fitTrigger, setFitTrigger] = useState(0);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const listQuery = useMemo(() => {
        // map=true → slim, map-only payload (coords + card fields, no per-row
        // inspection/document/owner queries) so the listings load fast.
        const base = { agency_wide: true, exclude_sold: "true", map: "true" };
        if (debouncedSearch.trim()) base.search = debouncedSearch.trim();
        if (propertyType) base.category = propertyType;
        if (minBeds) base.min_beds = minBeds;

        const band = priceBandToParams(priceBand);
        if (band.min_price != null) base.min_price = band.min_price;
        if (band.max_price != null) base.max_price = band.max_price;

        return base;
    }, [debouncedSearch, propertyType, minBeds, priceBand]);

    const listQueryKey = useMemo(() => JSON.stringify(listQuery), [listQuery]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setLoadingMore(false);
            setError(null);
            try {
                const res1 = await getProperties({ page: 1, page_size: 250, ...listQuery });
                if (cancelled) return;

                const data1 = res1.data || {};
                const batch1 = (data1.results || []).filter((item) => !isBuyerPortalLead(item));
                const total = typeof data1.count === "number" ? data1.count : batch1.length;

                setProperties(batch1);
                setTotalCount(total);
                setFitTrigger((n) => n + 1);
                setLoading(false);

                let next = data1.next;
                let page = 2;
                if (!next || batch1.length === 0 || cancelled) return;

                setLoadingMore(true);
                try {
                    while (next && page <= 40 && !cancelled) {
                        const res = await getProperties({ page, page_size: 250, ...listQuery });
                        if (cancelled) return;
                        const data = res.data || {};
                        const batch = (data.results || []).filter((item) => !isBuyerPortalLead(item));
                        if ((data.results || []).length === 0) break;
                        if (batch.length) setProperties((prev) => [...prev, ...batch]);
                        next = data.next;
                        page += 1;
                    }
                } finally {
                    if (!cancelled) setLoadingMore(false);
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setError(e.message || "Failed to load properties");
                    setProperties([]);
                    setTotalCount(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [listQueryKey, listQuery]);

    const [geocodedById, setGeocodedById] = useState({});
    const geocodeFetchedRef = useRef(new Set());

    useEffect(() => {
        geocodeFetchedRef.current = new Set();
        setGeocodedById({});
    }, [listQueryKey]);

    useEffect(() => {
        if (!MAPBOX_TOKEN || properties.length === 0) return undefined;
        let cancelled = false;

        (async () => {
            for (const p of properties) {
                if (cancelled) return;
                if (hasValidCoords(p)) continue;
                if (geocodeFetchedRef.current.has(p.id)) continue;

                const q = buildGeocodeQuery(p);
                if (!q || q.length < 6) {
                    geocodeFetchedRef.current.add(p.id);
                    setGeocodedById((prev) => ({ ...prev, [p.id]: null }));
                } else {
                    // Don't mark as fetched until the result is stored — otherwise a paginated
                    // refresh that cancels mid-fetch leaves the id flagged with no coords,
                    // and the next effect run skips it (pins silently disappear from the map).
                    try {
                        const stateKey = (p.state || "").trim().toUpperCase();
                        const bbox = STATE_BBOX[stateKey];
                        const bboxParam = bbox ? `&bbox=${bbox.join(",")}` : "";
                        const url =
                            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${encodeURIComponent(
                                MAPBOX_TOKEN
                            )}&country=AU&limit=1${bboxParam}`;
                        const res = await fetch(url);
                        if (cancelled) return;
                        const data = await res.json();
                        if (cancelled) return;
                        const c = data.features?.[0]?.center;
                        if (c && Array.isArray(c) && c.length >= 2) {
                            setGeocodedById((prev) => ({
                                ...prev,
                                [p.id]: { lng: Number(c[0]), lat: Number(c[1]) },
                            }));
                        } else {
                            setGeocodedById((prev) => ({ ...prev, [p.id]: null }));
                        }
                        geocodeFetchedRef.current.add(p.id);
                    } catch {
                        if (cancelled) return;
                        setGeocodedById((prev) => ({ ...prev, [p.id]: null }));
                        geocodeFetchedRef.current.add(p.id);
                    }
                }

                await new Promise((r) => setTimeout(r, 110));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [properties, listQueryKey]);

    const propertiesWithCoords = useMemo(() => {
        return properties
            .map((p) => {
                const c = resolveCoords(p, geocodedById);
                if (!c) return null;
                return { ...p, latitude: c.lat, longitude: c.lng };
            })
            .filter(Boolean);
    }, [properties, geocodedById]);

    const geocodingPending = useMemo(
        () =>
            Boolean(
                MAPBOX_TOKEN &&
                    properties.some((p) => {
                        if (hasValidCoords(p)) return false;
                        return !(p.id in geocodedById);
                    })
            ),
        [properties, geocodedById]
    );

    const missingCoordCount = useMemo(
        () => properties.filter((p) => resolveCoords(p, geocodedById) === null).length,
        [properties, geocodedById]
    );

    const handleEdit = useCallback(
        (property) => {
            // A CRM Property with no resolvable REA link (blank rea_listing_id, or the
            // "REA_<pk>" published-marker that matches no RealEstateProperty) 404s in
            // the Collings editor's crm-{id} resolver — route those to the standard CRM
            // property editor, which loads by Property id. Mirrors CollingsPropertiesPage.
            const rid = property?.rea_listing_id;
            const hasReaLink = !!rid && !String(rid).startsWith("REA_");
            if (property?.is_crm_property !== false && !hasReaLink) {
                navigate(`/properties/edit/${property.id}`);
                return;
            }
            const editId = property?.is_crm_property === false ? property.id : `crm-${property.id}`;
            navigate(`/collings-properties/edit/${editId}`);
        },
        [navigate]
    );

    const showInitialOverlay = loading && properties.length === 0 && !error;

    if (!MAPBOX_TOKEN) {
        return (
            <div className="min-h-screen bg-white p-0 text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6">
                <div className="flex flex-col items-center justify-center py-24 px-6">
                    <p className="max-w-lg text-center text-[15px] font-semibold text-[#36423F]">
                        Add a Mapbox access token
                    </p>
                    <p className="mt-3 max-w-lg text-center text-[13px] leading-relaxed text-gray-500">
                        Create a public access token (starts with <code className="text-[12px]">pk.</code>) at{" "}
                        <a
                            href="https://account.mapbox.com/access-tokens/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#55C5D0] hover:underline"
                        >
                            mapbox.com
                        </a>
                        , then set{" "}
                        <code className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[12px]">
                            REACT_APP_MAPBOX_ACCESS_TOKEN
                        </code>{" "}
                        in <code className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[12px]">.env</code>
                        , restart the dev server, and reload.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6 w-auto max-w-full overflow-x-hidden flex flex-col">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-[#F8F8F8] px-4 sm:px-6 lg:px-8 py-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-[#55C5D0]">Properties Map</h1>
                <button
                    onClick={() => navigate("/collings-properties")}
                    className="inline-flex h-10 items-center gap-2 rounded-full border border-gray-200 bg-white px-5 text-sm font-bold text-[#36423F] hover:border-[#55C5D0] hover:text-[#55C5D0]"
                >
                    <LayoutList className="w-4 h-4" />
                    <span>List view</span>
                </button>
            </div>

            {/* Filter bar */}
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-3 py-2.5">
                <div className="flex h-10 min-w-[160px] flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3">
                    <Search size={16} className="flex-shrink-0 text-gray-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Map area — search address or suburb"
                        className="min-w-0 flex-1 border-0 bg-transparent text-[13px] font-medium text-[#36423F] outline-none placeholder:text-gray-400"
                    />
                </div>

                <select
                    className={selectClass}
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    aria-label="Property type"
                >
                    {PROPERTY_TYPE_OPTIONS.map((o) => (
                        <option key={o.value || "any"} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>

                <select
                    className={selectClass}
                    value={priceBand}
                    onChange={(e) => setPriceBand(e.target.value)}
                    aria-label="Price"
                >
                    {PRICE_OPTIONS.map((o) => (
                        <option key={o.value || "any"} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>

                <select
                    className={selectClass}
                    value={minBeds}
                    onChange={(e) => setMinBeds(e.target.value)}
                    aria-label="Bedrooms"
                >
                    {BED_OPTIONS.map((o) => (
                        <option key={o.value || "any"} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>

                {loadingMore ? (
                    <span className="flex items-center gap-1.5 text-[12px] text-gray-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#55C5D0]" />
                        Loading more listings…
                    </span>
                ) : null}
                {geocodingPending && !loadingMore ? (
                    <span className="flex items-center gap-1.5 text-[12px] text-gray-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#55C5D0]" />
                        Locating addresses…
                    </span>
                ) : null}
            </div>

            {/* Map area */}
            <div className="relative w-full" style={{ height: "calc(100vh - 180px)", minHeight: "420px" }}>
                {showInitialOverlay ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#EEF2F6]">
                        <Loader2 className="h-8 w-8 animate-spin text-[#55C5D0]" />
                        <span className="mt-3 text-[13px] text-gray-500">Loading listings…</span>
                    </div>
                ) : error ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#EEF2F6] p-6 text-center">
                        <p className="text-[14px] text-gray-500">{error}</p>
                    </div>
                ) : (
                    <>
                        <MapView
                            accessToken={MAPBOX_TOKEN}
                            propertiesWithCoords={propertiesWithCoords}
                            totalCount={totalCount}
                            fitTrigger={fitTrigger}
                            onMarkerSelectRef={onMarkerSelectRef}
                        />
                        {missingCoordCount > 0 ? (
                            <div className="absolute bottom-3 left-3 z-[1] max-w-sm rounded-lg border border-gray-100 bg-white/95 px-3 py-2 text-[11px] text-gray-500 shadow">
                                {missingCoordCount} listing{missingCoordCount === 1 ? "" : "s"} could not be placed on the
                                map (address missing or not found).
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            {/* Selected-marker property card */}
            {mapCardProperty ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Property details"
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setMapCardProperty(null)}
                >
                    <div className="relative w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            aria-label="Close"
                            className="absolute -right-1 -top-1 z-[1] flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-[#36423F] shadow-md hover:bg-gray-50"
                            onClick={() => setMapCardProperty(null)}
                        >
                            <X size={18} />
                        </button>
                        <PropertyCard
                            key={mapCardProperty.id}
                            property={mapCardProperty}
                            basePath="/collings-properties"
                            onEdit={handleEdit}
                        />
                        <div className="mt-3 flex justify-center">
                            <button
                                type="button"
                                className="text-[13px] font-semibold text-[#55C5D0] hover:underline"
                                onClick={() => {
                                    navigate(`/collings-properties/${mapCardProperty.id}`);
                                    setMapCardProperty(null);
                                }}
                            >
                                View full listing — {formatShortAddress(mapCardProperty)}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CollingsPropertiesMapPage;
